import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CommonServiceService } from '../../../../utilities/services/common-service/common-service.service';
import { playerAbbr } from '../../../../utilities/utils/player-abbr.util';
import { MatchStatus } from '../../../../models/common/match-status.enum';
import { EmptyStateComponent } from '../../../shared/empty-state/empty-state.component';
import { LogoToneDirective } from '../../../../utilities/directive/logo_tone_directive/logo-tone.directive';

/**
 * One tab of the dialog's optional leg/golden-match switcher (Schedule tab
 * only — Results has no legs, so it always passes an empty `tabs` array and
 * the strip renders nothing).
 */
export interface TeamSubMatchDialogTab {
  type: 'leg' | 'golden';
  number?: number;
  active: boolean;
}

/**
 * Template 1's "View Sub-Matches" dialog — a per-board data table
 * (#/Team A/vs/Team B/Result/Score/Total Team Score) for a team tie.
 * Single source of truth for what used to be two byte-for-byte copies
 * (events-results' `.t1-evr-subdlg` and schedule's `.t1-sch-subdlg`) —
 * both call sites now render this component instead of maintaining their
 * own markup/styles by hand.
 *
 * Purely presentational: fetching sub-match data, tracking which leg is
 * selected, and opening/closing the dialog stay owned by each parent.
 */
@Component({
  selector: 'stupa-team-sub-match-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, EmptyStateComponent, LogoToneDirective],
  templateUrl: './team-sub-match-dialog.component.html',
  styleUrl: './team-sub-match-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Reparented to `.t1-app` in ngOnInit — global styles keep matching after the move.
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'stupa-team-sub-match-dialog',
    '(document:keydown.escape)': 'closeDialog.emit()',
  },
})
export class TeamSubMatchDialogComponent implements OnInit {
  private readonly commonService = inject(CommonServiceService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly doc = inject(DOCUMENT);

  /**
   * Re-parents the whole dialog to the app shell root.
   *
   * The overlay is `position: fixed`, but ANY ancestor with a `transform`,
   * `filter`, `backdrop-filter` or `zoom` becomes its containing block — so the
   * "full-screen" backdrop instead sized itself to whatever card/carousel host
   * happened to be above it (live-score cards set `backdrop-filter` and a hover
   * `transform`), leaving the panel clipped and the page behind it undimmed.
   * Being a sibling of `.t1-sc` was not enough; only leaving the subtree is.
   *
   * `.t1-app` rather than `<body>`: the phone layer scopes its rules to
   * `.t1-app.t1-app` (e.g. the `.t1-tsmd-tabs` scrolling strip), which appending
   * straight to `<body>` would drop. Angular removes the host from its *live*
   * parent on destroy, so the move needs no manual teardown.
   */
  ngOnInit(): void {
    (this.doc.querySelector('.t1-app') ?? this.doc.body).appendChild(this.host.nativeElement);
  }

  /** `.data-abbr` chip text (reference `playerAbbr()`) — same convention as the Upcoming Schedule cards / common-score-cards. */
  protected readonly playerAbbr = playerAbbr;

  /**
   * Resolves the real Member Association/Club abbreviation for one sub-match
   * player from that side's `selected_parents`. Injected by the parent rather
   * than computed here: the roles worth showing come from the event's
   * `displayableEntity`/`displayableEntity2` config, which each host component
   * already owns (see `getParentBadge()` / `getParentBadgeForDoublesPlayer()`).
   *
   * Defaults to '' so an unbound host silently falls back to
   * {@link playerAbbr}'s name-derived tag instead of erroring.
   *
   * @param side - The sub-match side (carries `selected_parents`)
   * @param playerIndex - Index within a doubles pair; 0 for singles
   * @returns Abbreviation such as "ESP" or "ESP/CTM", or '' when unavailable
   */
  @Input() entityAbbr: (side: unknown, playerIndex: number) => string = () => '';

  @Input() loading = false;
  /** Placeholder rows for the loading skeleton (both the desktop table and the phone card list). */
  readonly t1TsmdSkeletonRows: ReadonlyArray<number> = [0, 1, 2, 3];
  @Input() teamNameA = '';
  @Input() teamNameB = '';
  @Input() teamAForfeited = false;
  @Input() teamBForfeited = false;
  @Input() teamAScore: string | number = '';
  @Input() teamBScore: string | number = '';
  @Input() teamFlagA: string | null = null;
  @Input() teamFlagB: string | null = null;
  @Input() subDlgDate: string | Date | null = null;
  @Input() subDlgTime: string | Date | null = null;
  @Input() subMatches: any[] = [];
  /** Leg/Golden tab strip; empty (the default) hides it entirely. */
  @Input() tabs: TeamSubMatchDialogTab[] = [];

  @Output() closeDialog = new EventEmitter<void>();
  @Output() tabSelect = new EventEmitter<number>();

  /** @returns Player/side flag URL for a sub-match row, or the fallback flag. */
  get_flag(info: any, is_bye = false, index = 0): string {
    return this.commonService.get_selected_parents_flag(info, is_bye, index);
  }

  isParticipantForfeited(participant: any): boolean {
    return !!(
      participant?.participant_meta_data?.forfeit?.is_forfeited ||
      participant?.meta_data?.forfeit?.is_forfeited ||
      participant?.forfeit?.is_forfeited ||
      participant?.is_forfeited
    );
  }

  /**
   * Abbreviates a sub-match player's name to "First L." for the dialog's
   * compact name cell.
   * @param name - Full player name
   * @returns Abbreviated form, or '' when name is empty
   */
  subDlgPlayerName(name: string): string {
    if (!name) {
      return '';
    }
    let result = '';
    name.split(' ').forEach((part, index) => {
      result += index === 0 ? part : ` ${part[0]}.`;
    });
    return result;
  }

  hasSubMatchSetScores(match: any): boolean {
    const leftPoints = match?.participants?.[0]?.points;
    const rightPoints = match?.participants?.[1]?.points;
    if (!Array.isArray(leftPoints) || !Array.isArray(rightPoints)) {
      return false;
    }
    const len = Math.min(leftPoints.length, rightPoints.length);
    for (let i = 0; i < len; i++) {
      if (!(leftPoints[i] === 0 && rightPoints[i] === 0)) {
        return true;
      }
    }
    return false;
  }

  /** Set columns where both sides scored 0 are unplayed and stay hidden. */
  isSubMatchSetPlayed(match: any, setIndex: number): boolean {
    const left = match?.participants?.[0]?.points?.[setIndex];
    const right = match?.participants?.[1]?.points?.[setIndex];
    return !(left === 0 && right === 0);
  }

  /** Highlights the individual set scores this side took. */
  isSubMatchSetWonBySide(match: any, sideIndex: number, setIndex: number): boolean {
    const sides = match?.participants;
    if (!sides?.[0] || !sides?.[1]) {
      return false;
    }
    const opponentIndex = sideIndex === 0 ? 1 : 0;
    const own = sides[sideIndex]?.points?.[setIndex];
    const other = sides[opponentIndex]?.points?.[setIndex];
    return typeof own === 'number' && typeof other === 'number' && own > other;
  }

  /**
   * Side total for a sub-match's scoreboard (sets won, or match points for
   * team ties on a set-point system); null when neither side has scored yet,
   * so the caller can show a dash instead of "0 - 0".
   * @param match - Sub-match item with `participants[].sets_won`/`match_points`
   * @param sideIndex - 0 or 1, the side to read
   * @returns That side's total, or null when unplayed
   */
  subMatchSideTotal(match: any, sideIndex: number): number | null {
    const sides = match?.participants;
    if (!sides?.[0] || !sides?.[1]) {
      return null;
    }
    const useMatchPoints = match?.type === 2 && match?.point_system === 'set_point_system';
    const left = useMatchPoints ? (sides[0].match_points ?? 0) : (sides[0].sets_won ?? 0);
    const right = useMatchPoints ? (sides[1].match_points ?? 0) : (sides[1].sets_won ?? 0);
    if (left === 0 && right === 0) {
      return null;
    }
    const side = sides[sideIndex];
    if (!side) {
      return null;
    }
    return useMatchPoints ? (side.match_points ?? 0) : (side.sets_won ?? 0);
  }

  /** The two sub-match sides, so the phone card can render both from one template. */
  protected readonly sides = [0, 1];

  /**
   * @returns Whether this side won the sub-match outright (decided result only).
   */
  isSideWinner(match: any, sideIndex: number): boolean {
    const winner = match?.winner;
    return winner != null && winner === match?.participants?.[sideIndex]?.participant_id;
  }

  /** @returns Whether {@link subMatchSideTotal} has a value for this side */
  hasSubMatchSideTotal(match: any, sideIndex: number): boolean {
    return this.subMatchSideTotal(match, sideIndex) !== null;
  }

  /** Status dot for a sub-match dialog row — live / done / not yet played. */
  subMatchStatusClass(sub: any): 'live' | 'done' | 'wait' {
    if (this.matchLifecycleStatus(sub) === 'live') {
      return 'live';
    }
    if (this.hasSubMatchSideTotal(sub, 0) || this.hasSubMatchSideTotal(sub, 1)) {
      return 'done';
    }
    return 'wait';
  }

  /** Maps API match lifecycle to a subtle status badge (live / upcoming / completed). */
  private matchLifecycleStatus(
    match: { status?: string; live_score?: boolean } | null | undefined
  ): 'live' | 'upcoming' | 'completed' | null {
    if (!match?.status) {
      return null;
    }
    const { status, live_score: liveScore } = match;
    if (
      status === MatchStatus.IN_PROGRESS ||
      (status === MatchStatus.SCORED && liveScore === true)
    ) {
      return 'live';
    }
    if (status === MatchStatus.SCORED && liveScore === false) {
      return 'completed';
    }
    if (status === MatchStatus.NOT_STARTED) {
      return 'upcoming';
    }
    return null;
  }

  /**
   * Running "boards won" tally after each sub-match, for the dialog's Total
   * Team Score column — a board only counts once it has a decided result
   * (both side totals present and unequal); an unplayed board repeats the
   * previous running score, same as a real tie sheet.
   */
  get subDlgRunningTotals(): { a: number; b: number }[] {
    let a = 0;
    let b = 0;
    return (this.subMatches ?? []).map((sub: any) => {
      const totalA = this.subMatchSideTotal(sub, 0);
      const totalB = this.subMatchSideTotal(sub, 1);
      if (totalA != null && totalB != null && totalA !== totalB) {
        if (totalA > totalB) {
          a++;
        } else {
          b++;
        }
      }
      return { a, b };
    });
  }
}
